import styled from '@emotion/styled';
import React, { useEffect } from 'react';
import { AppConfig } from '../../app.config';
import { ReactComponent as WallySunglassesImage } from '../../assets/Wally_Sunglasses.svg';
import { ReactComponent as Confetti } from '../../assets/confetti.svg';
import { ReactComponent as WallyHappy } from '../../assets/wally_happy.svg';
import { colors, fontFamilies } from '../../components/baseStyle/Variables';
import Button from '../../components/button/Button';
import { FormNavButtonWrapper } from '../../components/formElements/FormElements';
import LeaseTermsTooltip from '../../components/lease-terms-tooltip/lease-terms-tooltip';
import { Application, CustomerEligibility, Dealer } from '../../gql/graphql';
import { convertToCurrency } from '../../helpers/converters/converters';
import { getCurrentApplyType, isCurrentApplyTypeOTB } from '../../helpers/converters/currentApplyType';
import { Role } from '../../types/role';
import { getEcommApprovalOnly, selectApprovalLeaseOptions, selectLeaseHelper } from '../../helpers/leases/leases.helpers';
import { SegmentProps } from '../../state-machine/actions/segment';
import { Trans, useTranslation } from 'react-i18next';

type Props = SegmentProps & {
  orderId: string;
  application: Application | undefined;
  dealer: Dealer;
  eligibilityDetails?: CustomerEligibility;
  role: Role;
};

const ApprovalSubText = styled.div`
  color: ${colors.darkBlue};
  font-family: ${fontFamilies.interLight};
  font-size: 1.1rem;
  text-align: left;
  margin-top: 1rem;
  max-width: 65%;
`;

const CurrencyStyle = styled.div`
  font-family: ${fontFamilies.interBold};
  line-height: 1.4;
  font-size: 1.75rem;
`;

const Gradient = styled.div`
  position: absolute;
  left: -2rem;
  right: -2rem;
  width: calc(100% + 4rem);
  max-width: 465px;
  margin: 0 auto;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0) 100%);
  margin-top: 1.5rem;
  height: 50px;
`;

const WallyImgStyle = styled.div`
  position: absolute;
  width: 5rem;
  right: 1rem;
  bottom: -2.4rem;
  z-index: 1; // without this, the gradient overlaps wally's feet
`;
const ConfettiStyle = styled.div<{ prequal?: boolean }>`
  position: absolute;
  width: 4rem;
  right: 2rem;
  bottom: 100%;
`;

const getWallyImg = (isOtb: boolean) =>
  isOtb ? (
    <WallyImgStyle>
      <WallySunglassesImage />
    </WallyImgStyle>
  ) : (
    <>
      <ConfettiStyle>
        <Confetti />
      </ConfettiStyle>
      <WallyImgStyle>
        <WallyHappy />
      </WallyImgStyle>
    </>
  );

const ApprovalConfirmation: React.FC<Props> = ({ orderId, application, dealer, eligibilityDetails, role, track, page }) => {
  const { t } = useTranslation();
  const ecommApprovalOnly = getEcommApprovalOnly(dealer);
  const applicationNumber = application?.lease?.displayId;

  const isOtb = isCurrentApplyTypeOTB(getCurrentApplyType(eligibilityDetails)) || !!application?.lease?.isOtb || !!application?.loan?.isOtb;

  const leaseOptions = selectApprovalLeaseOptions(application?.lease?.options, eligibilityDetails?.otbProducts?.leaseOptions);
  const approvedAmount = selectLeaseHelper(leaseOptions)?.approvedAmount;

  useEffect(() => {
    page('Approval Confirmation', {
      orderId: orderId,
      dealerId: dealer.id,
      approvedAmount: approvedAmount,
    });
    if (isOtb) {
      track('OTB Lease Approval Confirmed');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  type PageText = {
    buttonText: string | null;
    pageText: JSX.Element | null;
  };

  const getContinueText = () => {
    if (ecommApprovalOnly) {
      return (
        <Trans i18nKey={($) => $.pageApprovalConfirmation.keepShopping} values={{ dealerName: dealer.friendlyName }} components={{ strong: <strong /> }} />
      );
    } else {
      return (
        <>
          <Trans i18nKey={($) => $.pageApprovalConfirmation.talkToSalesAssociate} values={{ applicationNumber }} components={{ strong: <strong /> }} />
        </>
      );
    }
  };

  const getScreenText = (): PageText => {
    if (role === Role.Customer) {
      return {
        buttonText: null,
        pageText: <h6>{getContinueText()}</h6>,
      };
    } else {
      return {
        buttonText: t(($) => $.pageApprovalConfirmation.openApplication),
        pageText: <h6>{t(($) => $.pageApprovalConfirmation.openApplicationToFinalize)}</h6>,
      };
    }
  };

  const handleSubmit = () => {
    window.location.href = `${AppConfig.dealerPortalUrl}/PurchasedArticlesForm/Index?publicId=${application?.lease?.publicId}`;
  };

  return (
    <div>
      <h1 style={{ fontSize: '3.5rem' }}>
        {isOtb ? t(($) => $.pageApprovalConfirmation.approvalConfirmed) : t(($) => $.pageApprovalConfirmation.youAreApproved)}
      </h1>
      <div style={{ display: 'flex', position: 'relative' }}>
        {getWallyImg(isOtb)}
        <ApprovalSubText>
          <React.Fragment>
            <Trans
              i18nKey={($) => $.pageApprovalConfirmation.forOurFinancingUpTo}
              components={{
                leaseTerms: <LeaseTermsTooltip styling={{ fontSize: '1.1rem' }} />,
                div1: <div />,
                div2: <div />,
              }}
            />
          </React.Fragment>
          <CurrencyStyle>
            {isOtb
              ? convertToCurrency(parseFloat(approvedAmount!)) // TODO: validation?
              : convertToCurrency(parseFloat(approvedAmount!), 0)}
          </CurrencyStyle>
        </ApprovalSubText>
      </div>
      <Gradient />
      <div style={{ marginTop: '3rem' }}>
        {getScreenText().pageText}

        <div style={{ marginTop: '2rem' }}>
          {!!getScreenText().buttonText && (
            <FormNavButtonWrapper>
              <Button onClick={handleSubmit}>{getScreenText().buttonText}</Button>
            </FormNavButtonWrapper>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalConfirmation;
